import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Play, Sparkles, Share2, Linkedin, Facebook, Instagram, Music2, Download, Copy, Check, Volume2, VolumeX } from "lucide-react";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";

/**
 * Dashboard welcome video — first-time user onboarding reel.
 *
 * The MP4 lives at /public/brand/welcome-video.mp4 — produced via
 * HeyGen using the v2 dual-purpose script in
 * /public/brand/welcome-video-script.md. The current asset is
 * **9:16 vertical (720×1280)** because the same render doubles as
 * a marketing reel for Instagram / Shorts / LinkedIn — one render,
 * two distribution surfaces.
 *
 * The card itself now plays the video muted-and-looping inside the
 * poster slot, so it doubles as a tiny billboard for the Live Doubt
 * Solver before the user even clicks. Static-poster fallback kicks
 * in if the browser blocks autoplay.
 *
 * Repost / share: the modal exposes a Web Share API button (native on
 * mobile — opens IG/TikTok/LinkedIn/X picker), platform-specific
 * deep-links (LinkedIn, X, WhatsApp), copy-link, and a direct
 * download for users who want to upload the MP4 manually to
 * platforms like TikTok / Instagram Reels that don't accept URL
 * shares.
 */
const VIDEO_URL = "/brand/welcome-video.mp4";
const POSTER_URL = "/brand/welcome-video-poster.jpg";

const SHARE_TITLE = "Oak AI Campus — Master anything with AI";
const SHARE_TEXT = "I just discovered an AI study app that builds you a 5-step course on any topic — and a Live Doubt Solver that knows your exact course material. Wild.";
const SHARE_HASHTAGS = "OAKAcademy,AILearning,EdTech,StudyTok,LearnWithAI";

export default function WelcomeVideoCard() {
  const [open, setOpen] = useState(false);
  const [videoAvailable, setVideoAvailable] = useState(null); // null=checking, true=exists, false=placeholder
  const [previewMuted, setPreviewMuted] = useState(true);
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const halfFiredRef = useRef(false);

  // Public origin = base URL of the deployed site. We construct a
  // canonical share URL pointing at oakaiapp.com (or the current
  // origin in dev) so social previews resolve to the brand domain.
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://oakaiapp.com";

  useEffect(() => {
    // Probe whether the MP4 has been uploaded yet. HEAD request avoids
    // downloading the video just to check existence. Some CDNs (Cloudflare,
    // Vercel's edge) re-serve `.mp4` as `application/octet-stream` instead
    // of `video/mp4`, so we accept any 2xx response — the <video> element
    // will refuse to play a non-video anyway, which is the real safety net.
    fetch(VIDEO_URL, { method: "HEAD" })
      .then((r) => setVideoAvailable(r.ok))
      .catch(() => setVideoAvailable(false));
  }, []);

  // While the modal is OPEN we completely unmount the inline preview
  // (return a static poster instead). Reason: when both <video> elements
  // pointed at the same 65 MB MP4, mobile browsers had to share a single
  // hardware decoder + a finite media-buffer pool — the modal video would
  // pause/stutter every few seconds as the inline loop competed for the
  // pipe (Realme GT 7 + iOS Safari both reported by user Apr 28).
  // Unmounting frees the decoder slot AND cancels the inline's network
  // requests so the modal gets the entire bandwidth budget.

  const play = () => {
    setOpen(true);
    halfFiredRef.current = false;
    trackEvent("welcome_video_opened", { source: "dashboard" });
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = v.currentTime / v.duration;
    if (pct >= 0.5 && !halfFiredRef.current) {
      halfFiredRef.current = true;
      trackEvent("welcome_video_50pct");
    }
  };

  const onEnded = () => {
    // Per Apr 28 2026 user spec:
    //   * Card on the dashboard stays permanently (we never set `dismissed`)
    //   * BUT the play-with-sound modal auto-closes after the video ends
    //     so the user is brought back to the dashboard without needing
    //     to tap the dialog X. The inline preview keeps looping behind.
    trackEvent("welcome_video_completed");
    setTimeout(() => setOpen(false), 600);
  };

  return (
    <>
      {/* Permanent dashboard onboarding card — no dismiss button per user spec.
          Stays put forever so first-time AND returning users always have a
          one-click way to re-watch + re-share the welcome reel.
          `max-w-full` + outer wrapper guards against horizontal bleed on
          small Android screens (Realme GT 7 reported overflow on Apr 28). */}
      <section
        className="relative glass border border-white/10 rounded-2xl overflow-hidden group animate-fadeup max-w-full"
        data-testid="welcome-video-card"
      >

        <div className="grid md:grid-cols-[minmax(0,1fr)_220px] gap-0">
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <div className="label-eyebrow flex items-center gap-2 mb-2">
              <Sparkles size={12} className="text-[#00FF9D]" /> Welcome to Oak AI Campus
            </div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">
              Learn how to use Oak AI Campus learning app
            </h2>
            <p className="text-white/60 text-sm mt-2 max-w-md">
              A quick 60-second tour — AI Counselor, whitepapers, the Live Doubt Solver, and how to earn credits.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={play}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full btn-primary font-display font-semibold text-sm transition"
                data-testid="btn-welcome-play"
              >
                <Play size={14} fill="currentColor" /> Play with sound
              </button>
              {videoAvailable && (
                <button
                  type="button"
                  onClick={() => setPreviewMuted((m) => !m)}
                  className="h-9 w-9 grid place-items-center rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
                  aria-label={previewMuted ? "Unmute preview" : "Mute preview"}
                  data-testid="btn-welcome-mute-toggle"
                  title={previewMuted ? "Unmute the preview" : "Mute the preview"}
                >
                  {previewMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
              )}
            </div>
          </div>

          {/* Inline autoplaying mini-billboard. Desktop: tall 9:16 right-rail
              column (220px). Mobile: stretches under the copy at a capped
              height so it doesn't blow out the page on small Android phones
              (Realme GT 7 reported viewport bleed Apr 28). Width is locked
              to the parent grid cell so we never overflow. */}
          <button
            type="button"
            onClick={play}
            className="relative w-full aspect-[9/16] max-h-[480px] md:max-h-none md:aspect-auto md:min-h-[300px] cursor-pointer group bg-black/40 overflow-hidden"
            data-testid="btn-welcome-poster"
            aria-label="Play welcome video"
          >
            {videoAvailable && !open ? (
              <video
                ref={previewRef}
                src={VIDEO_URL}
                poster={POSTER_URL}
                muted={previewMuted}
                loop
                autoPlay
                playsInline
                preload="metadata"
                disablePictureInPicture
                disableRemotePlayback
                className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
                aria-hidden="true"
                data-testid="welcome-video-preview"
              />
            ) : (
              <img
                src={POSTER_URL}
                alt="Oak AI Campus welcome video preview"
                className="absolute inset-0 w-full h-full object-cover object-center"
                aria-hidden={open}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/55" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-white/95 grid place-items-center group-hover:scale-110 transition-transform shadow-2xl">
                <Play size={24} fill="currentColor" className="text-[#0B0E14] ml-1" />
              </div>
            </div>
          </button>
        </div>

        {/* Share bar — always visible on the dashboard, no need to open
            the modal. Guarantees Instagram / TikTok / LinkedIn / Facebook
            buttons sit directly under the video tile per Apr 28 spec. */}
        {videoAvailable !== false && <ShareBar shareUrl={shareUrl} />}
      </section>

      {/* Video modal — phone-frame width to match the 9:16 vertical asset.
          `min(460px, 100vw - 1rem)` is more reliable than calc on Android
          Chrome where 100vw can include the scrollbar gutter and bleed. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="glass border-white/10 p-0 overflow-hidden"
          style={{ width: "min(460px, 100vw - 1rem)", maxWidth: "min(460px, 100vw - 1rem)" }}
          data-testid="welcome-video-modal"
        >
          <DialogTitle className="sr-only">Oak AI Campus welcome video</DialogTitle>
          {videoAvailable === false ? (
            <div className="aspect-[9/16] grid place-items-center p-10 text-center">
              <div>
                <div className="h-14 w-14 mx-auto rounded-full bg-white/10 grid place-items-center mb-3">
                  <Sparkles size={22} className="text-[#00FF9D]" />
                </div>
                <div className="font-display text-xl">Welcome video coming soon</div>
                <div className="text-white/55 text-sm mt-2 max-w-md mx-auto">
                  We're putting the finishing touches on a 60-second guided tour. In the meantime, the Dashboard shortcut cards below cover every feature.
                </div>
              </div>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={VIDEO_URL}
                poster={POSTER_URL}
                controls
                autoPlay
                playsInline
                preload="auto"
                disableRemotePlayback
                className="w-full h-auto bg-black aspect-[9/16] object-contain"
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                data-testid="welcome-video-player"
              />
              <ShareBar shareUrl={shareUrl} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Repost bar — sits beneath the modal player. One-tap distribution to
 * Instagram, LinkedIn, Facebook, and TikTok plus a native share fall-back.
 *
 * Platform reality check (2026):
 *   * **Instagram & TikTok** — neither has a public URL share endpoint
 *     that works from a browser. Their content has to be uploaded
 *     through the mobile app. Our buttons therefore (a) invoke the
 *     native Web Share API with the actual MP4 file (iOS/Android both
 *     surface IG/TikTok in the system share picker since 2023), and
 *     (b) auto-trigger a download of the MP4 as a fallback so users
 *     can hand-upload from desktop. We show a tiny instructional toast
 *     so the behaviour is never surprising.
 *   * **LinkedIn & Facebook** — both expose URL-based sharing endpoints
 *     (`/sharing/share-offsite`, `/sharer/sharer.php`). Tap opens a new
 *     window with the share dialog pre-filled.
 *   * The "Share to apps" primary button is the catch-all — fires the
 *     OS share sheet which lets the user pick any installed app
 *     (WhatsApp, Telegram, Discord, X, Snapchat, DMs, anything).
 */
function ShareBar({ shareUrl }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const cleanUrl = (shareUrl || "https://oakaiapp.com").replace(/\/$/, "");

  // Helper: try to push the actual MP4 file through the OS share sheet.
  // Falls back through URL share → clipboard so something always works.
  // Android in particular often rejects file-shares with no clear error
  // (Realme / OnePlus / Xiaomi all gate this differently per security
  // patch level), so we wrap each step in its own try/catch and never
  // surface raw failures to the user — only a graceful end-state.
  const shareFileOrFallback = async (surface, fallbackToDownload = false) => {
    setBusy(true);
    trackEvent("welcome_video_share_clicked", { surface });

    const hasShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
    const hasCanShare = typeof navigator !== "undefined" && typeof navigator.canShare === "function";

    // Step A — try sharing the MP4 file itself (best for IG / TikTok / WA media)
    if (hasCanShare) {
      try {
        const r = await fetch(VIDEO_URL);
        if (r.ok) {
          const blob = await r.blob();
          const file = new File([blob], "oak-academy-reel.mp4", { type: "video/mp4" });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file],
                title: SHARE_TITLE,
                text: `${SHARE_TEXT}\n\n${cleanUrl}`,
              });
              toast.success("Shared!");
              setBusy(false);
              return;
            } catch (e) {
              if (e?.name === "AbortError") { setBusy(false); return; } // user dismissed picker
              // Permission / NotAllowedError on Android — fall through to URL share
            }
          }
        }
      } catch {
        // network / blob / canShare crash — silently fall through
      }
    }

    // Step B — share just the URL + text (works on virtually every modern mobile)
    if (hasShare) {
      try {
        await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url: cleanUrl });
        toast.success("Shared!");
        setBusy(false);
        return;
      } catch (e) {
        if (e?.name === "AbortError") { setBusy(false); return; }
        // any other Share API failure — fall through to clipboard / download
      }
    }

    // Step C — desktop or no-Share-API fallbacks
    if (fallbackToDownload) {
      // Desktop fallback for IG/TikTok — both need a manual app upload.
      try {
        const a = document.createElement("a");
        a.href = VIDEO_URL;
        a.download = "oak-academy-reel.mp4";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("MP4 downloading — open the app and upload it.", { duration: 5500 });
      } catch {
        toast.error("Couldn't start the download. Long-press the video to save it manually.");
      }
      setBusy(false);
      return;
    }

    // Last resort — copy share text + URL.
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n\n${cleanUrl}`);
      toast.success("Copied to clipboard — paste anywhere.");
    } catch {
      // Some Android in-app browsers block clipboard writes too. Show
      // the URL plain so the user can long-press to copy it manually.
      toast.error("Copy blocked by browser. URL: " + cleanUrl, { duration: 8000 });
    }
    setBusy(false);
  };

  const onInstagram = async () => {
    // Instagram has no URL share endpoint. Mobile: native share sheet
    // (system menu shows "Share to Instagram" if app is installed).
    // Desktop: fall back to MP4 download since Reels uploads need the app.
    await shareFileOrFallback("instagram", /* fallbackToDownload */ true);
  };

  const onTikTok = async () => {
    // Same pattern as Instagram — no URL share endpoint exists. Mobile
    // gets the OS picker (TikTok shows up if installed); desktop gets
    // the download for manual upload.
    await shareFileOrFallback("tiktok", /* fallbackToDownload */ true);
  };

  const fireDeepLink = (surface, url) => {
    trackEvent("welcome_video_share_clicked", { surface });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cleanUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1800);
      trackEvent("welcome_video_share_clicked", { surface: "copy" });
    } catch {
      toast.error("Copy failed — please copy manually: " + cleanUrl);
    }
  };

  const downloadMp4 = () => {
    trackEvent("welcome_video_share_clicked", { surface: "download" });
    const a = document.createElement("a");
    a.href = VIDEO_URL;
    a.download = "oak-academy-reel.mp4";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Downloading — upload it to TikTok / Reels manually.", { duration: 5000 });
  };

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(cleanUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cleanUrl)}&quote=${encodeURIComponent(SHARE_TEXT)}`;

  return (
    <div className="px-4 py-3 border-t border-white/10 bg-[#0B0E14]/95" data-testid="welcome-video-share-bar">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="label-eyebrow text-white/55 flex items-center gap-1.5">
          <Share2 size={11} /> Repost this reel
        </div>
      </div>

      {/* Primary native share — most reliable on mobile */}
      <button
        type="button"
        onClick={() => shareFileOrFallback("native_primary")}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 btn-primary font-display text-xs font-semibold disabled:opacity-50 mb-2"
        data-testid="btn-share-native"
      >
        <Share2 size={13} />
        {busy ? "Preparing…" : "Share to apps · DMs"}
      </button>

      {/* Platform grid — Instagram, TikTok, LinkedIn, Facebook */}
      <div className="grid grid-cols-4 gap-2">
        <button
          type="button"
          onClick={onInstagram}
          disabled={busy}
          className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 bg-gradient-to-br from-[#feda75]/12 via-[#d62976]/12 to-[#962fbf]/12 border border-[#d62976]/35 text-[#ffadd1] hover:from-[#feda75]/22 hover:via-[#d62976]/22 hover:to-[#962fbf]/22 hover:text-white transition disabled:opacity-50"
          data-testid="btn-share-instagram"
          aria-label="Share to Instagram"
          title="Share to Instagram (Reels / Stories)"
        >
          <Instagram size={16} />
          <span className="text-[10px] font-display font-semibold">Instagram</span>
        </button>
        <button
          type="button"
          onClick={onTikTok}
          disabled={busy}
          className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 bg-white/8 border border-white/20 text-white/85 hover:bg-white/15 hover:text-white transition disabled:opacity-50"
          data-testid="btn-share-tiktok"
          aria-label="Share to TikTok"
          title="Share to TikTok"
        >
          <Music2 size={16} />
          <span className="text-[10px] font-display font-semibold">TikTok</span>
        </button>
        <button
          type="button"
          onClick={() => fireDeepLink("linkedin", linkedInUrl)}
          className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 bg-[#0A66C2]/15 border border-[#0A66C2]/40 text-[#9bc7ff] hover:bg-[#0A66C2]/25 hover:text-white transition"
          data-testid="btn-share-linkedin"
          aria-label="Share to LinkedIn"
          title="Share to LinkedIn"
        >
          <Linkedin size={16} />
          <span className="text-[10px] font-display font-semibold">LinkedIn</span>
        </button>
        <button
          type="button"
          onClick={() => fireDeepLink("facebook", facebookUrl)}
          className="flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 bg-[#1877F2]/15 border border-[#1877F2]/40 text-[#a8c8ff] hover:bg-[#1877F2]/25 hover:text-white transition"
          data-testid="btn-share-facebook"
          aria-label="Share to Facebook"
          title="Share to Facebook"
        >
          <Facebook size={16} />
          <span className="text-[10px] font-display font-semibold">Facebook</span>
        </button>
      </div>

      {/* Utility row */}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          type="button"
          onClick={copyLink}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 bg-white/5 border border-white/15 text-white/80 hover:bg-white/10 hover:text-white transition text-[11px] font-display font-semibold"
          data-testid="btn-share-copy"
        >
          {copied ? <Check size={12} className="text-[#00FF9D]" /> : <Copy size={12} />}
          {copied ? "Copied!" : "Copy link"}
        </button>
        <button
          type="button"
          onClick={downloadMp4}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 bg-[#ffb067]/12 border border-[#ffb067]/35 text-[#ffd6a8] hover:bg-[#ffb067]/22 hover:text-white transition text-[11px] font-display font-semibold"
          data-testid="btn-share-download"
          title="Download MP4 — manually upload to TikTok or Reels"
        >
          <Download size={12} /> Download MP4
        </button>
      </div>
      <p className="text-[10px] text-white/40 mt-2 leading-snug">
        Tip: Instagram &amp; TikTok need the MP4 uploaded inside their app — on mobile, tap their tile and pick the app from the share sheet; on desktop, use <span className="text-white/65 font-medium">Download MP4</span>.
      </p>
    </div>
  );
}
